"use client";

import { Donations } from "@/types/models/Donations";
import { cn } from "@/lib/utils";

type DonationTypeBadgeConfig = {
  dotClass: string;
  borderClass: string;
  labelClass: string;
  label: string;
  tooltip: string;
};

/** Resolved display key: cadence for recurring comes from `recurringPlans.interval` */
type DonationBadgeVariant =
  | "onetime"
  | "roundup"
  | "weekly"
  | "monthly"
  | "daily"
  | "yearly"
  | "recurring";

const BADGE_SHELL =
  "inline-flex items-center gap-2 rounded-full border bg-transparent px-2.5 py-0.5 text-xs font-semibold";

const DONATION_BADGE_BY_VARIANT: Record<
  DonationBadgeVariant,
  DonationTypeBadgeConfig
> = {
  onetime: {
    dotClass: "bg-zinc-400 dark:bg-zinc-500",
    borderClass: "border-zinc-500/45 dark:border-zinc-400/45",
    labelClass: "text-zinc-600 dark:text-zinc-400",
    label: "One-off",
    tooltip: "One-off donation",
  },
  weekly: {
    dotClass: "bg-emerald-600 dark:bg-emerald-500",
    borderClass: "border-emerald-600/45 dark:border-emerald-400/45",
    labelClass: "text-emerald-700 dark:text-emerald-400",
    label: "Weekly",
    tooltip: "Weekly donation",
  },
  monthly: {
    dotClass: "bg-primary",
    borderClass: "border-primary/45",
    labelClass: "text-primary",
    label: "Monthly",
    tooltip: "Monthly donation",
  },
  daily: {
    dotClass: "bg-chart-1",
    borderClass: "border-chart-1/45",
    labelClass: "text-chart-1",
    label: "Daily",
    tooltip: "Daily donation",
  },
  yearly: {
    dotClass: "bg-chart-1",
    borderClass: "border-chart-1/45",
    labelClass: "text-chart-1",
    label: "Yearly",
    tooltip: "Yearly donation",
  },
  recurring: {
    dotClass: "bg-chart-1",
    borderClass: "border-chart-1/45",
    labelClass: "text-chart-1",
    label: "Recurring",
    tooltip: "Recurring donation",
  },
  roundup: {
    dotClass: "bg-chart-5",
    borderClass: "border-chart-5/45",
    labelClass: "text-chart-5",
    label: "Round-up",
    tooltip: "Round-up donation",
  },
};

function resolveBadgeVariant(
  donation: Pick<Donations, "type" | "recurringPlans">
): DonationBadgeVariant {
  const { type, recurringPlans } = donation;

  if (type === "onetime") return "onetime";
  if (type === "roundup") return "roundup";

  if (type === "weekly" || type === "monthly") {
    return type;
  }

  if (type === "recurring") {
    const interval = recurringPlans?.interval;
    if (interval === "weekly") return "weekly";
    if (interval === "monthly") return "monthly";
    if (interval === "daily") return "daily";
    if (interval === "yearly") return "yearly";
    return "recurring";
  }

  return "recurring";
}

/** Recurring plans list: same shell as donation type badges, keyed by Stripe interval. */
export function RecurringPlanIntervalBadge({
  interval,
}: {
  interval: "daily" | "weekly" | "monthly" | "yearly";
}) {
  const variant: DonationBadgeVariant =
    interval === "daily"
      ? "daily"
      : interval === "weekly"
        ? "weekly"
        : interval === "monthly"
          ? "monthly"
          : "yearly";
  const config = DONATION_BADGE_BY_VARIANT[variant];

  return (
    <div className="flex">
      <div className={cn(BADGE_SHELL, config.borderClass, config.labelClass)}>
        <span
          className={cn("size-1.5 shrink-0 rounded-full", config.dotClass)}
          aria-hidden
        />
        <span>{config.label}</span>
      </div>
    </div>
  );
}

export type DonationTypeBadgeProps = {
  donation: Pick<Donations, "type" | "recurringPlans">;
};

export function DonationTypeBadge({ donation }: DonationTypeBadgeProps) {
  const variant = resolveBadgeVariant(donation);
  const config = DONATION_BADGE_BY_VARIANT[variant];

  return (
    <div className="flex">
      <div className={cn(BADGE_SHELL, config.borderClass, config.labelClass)}>
        <span
          className={cn("size-1.5 shrink-0 rounded-full", config.dotClass)}
          aria-hidden
        />
        <span>{config.label}</span>
      </div>
    </div>
  );
}
